import { useLoaderData } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import useTodayData from '../hooks/useTodayData';
import useTodayActions from '../hooks/useTodayActions';
import { useTodayWidgetsBundle } from '../hooks/useTodayWidgetsBundle';
import TaskModal from '../components/TaskModal';
import TaskLinkPickerModal from '../components/TaskLinkPickerModal';
import BatchSweepMode from '../components/BatchSweepMode';
import FollowUpModal from '../components/FollowUpModal';
import DelegateModal from '../components/DelegateModal';
import PlanDayPreviewModal from '../components/today/PlanDayPreviewModal';
import RealitySnapshot from '../components/today/RealitySnapshot';
import PendencyRadar from '../components/today/PendencyRadar';
import DecisionQueue from '../components/today/DecisionQueue';
import TimeExecutionRail from '../components/today/TimeExecutionRail';
import AIAttentionLayer from '../components/today/AIAttentionLayer';
import StakeholderHeat from '../components/today/StakeholderHeat';
import WeeklySummaryCard from '../components/today/WeeklySummaryCard';
import { RetryBanner } from '../components/common/RetryBanner';
import DaybreakBanner from '../components/daybreak/DaybreakBanner';

export default function TodayView({ onFocusTask, focusTaskId, daybreakPending, onOpenDaybreak, onDismissDaybreak }) {
  const loaderData = useLoaderData();
  const { activeBusiness, setActiveBusiness, getBusinessColor, getBusinessShortCode } = useBusiness();
  const { user } = useAuth();

  const data = useTodayData(loaderData);
  // Prime the right-rail widget caches from the loader's super-bundle BEFORE
  // children render — RealitySnapshot/StakeholderHeat/PendencyRadar will read
  // from cache on first paint instead of each firing their own request.
  useTodayWidgetsBundle(loaderData);
  const actions = useTodayActions({
    tasks: data.tasks, setTasks: data.setTasks,
    blocks: data.blocks, setBlocks: data.setBlocks,
    today: data.today,
    fetchAll: data.fetchAll, fetchDecisions: data.fetchDecisions, fetchTasks: data.fetchTasks,
  });

  return (
    <div className="h-full flex flex-col" data-testid="today-view" style={{ background: 'var(--pd-bg)' }}>

      <DaybreakBanner
        pending={daybreakPending}
        onStart={onOpenDaybreak}
        onDismiss={onDismissDaybreak}
      />

      <RealitySnapshot
        summary={data.decisionSummary}
        activeTasks={data.activeTasks.length}
        completedTasks={data.completedTasks.length}
        user={user}
        planningDay={actions.planningDay}
        onPlanDay={actions.handlePlanMyDay}
        onNewTask={() => actions.setShowModal(true)}
        urgentCount={data.urgentCount}
        todayCalls={data.todayCalls}
        overduePayments={data.overduePayments}
        businessPressure={data.businessPressure}
        activeBusiness={activeBusiness}
        onBusinessFilter={(biz) => setActiveBusiness(biz)}
      />

      {data.loadError ? (
        <RetryBanner
          testId="today-load-error"
          message="We couldn't load Today's data."
          hint="Decisions, blocks, and budgets are temporarily out of reach."
          onRetry={() => data.fetchAll()}
        />
      ) : null}

      <div className="px-4 pt-3">
        <WeeklySummaryCard />
      </div>

      <div className="flex-1 flex min-h-0">
        {/* LEFT: Pendency Radar */}
        <div className="hidden lg:block" style={{ width: 220, flexShrink: 0 }}>
          <PendencyRadar
            businessPressure={data.businessPressure}
            activeBusiness={activeBusiness}
            onBusinessFilter={(biz) => setActiveBusiness(biz)}
          />
        </div>

        {/* CENTER: Decision Queue */}
        <DecisionQueue
          decisions={data.decisions}
          focusTaskId={focusTaskId}
          onToggleComplete={actions.handleToggleComplete}
          onSelect={actions.handleSelectDecision}
          onDefer={actions.handleDeferTask}
          onSchedule={actions.handleSlotTask}
          onFocus={(item) => onFocusTask?.({
            id: item.id,
            title: item.title,
            subtasks: item.subtasks || [],
            estimatedMinutes: item.estimatedMinutes,
            businessId: item.businessId,
          })}
          onDelegate={(item) => actions.setDelegateItem(item)}
          onCallAction={actions.handleCallAction}
          onPaymentAction={actions.handlePaymentAction}
          onReadingAction={actions.handleReadingAction}
          onArchive={actions.handleArchiveDecision}
          onRename={actions.handleRenameDecision}
          onSweep={() => actions.setSweepMode(true)}
          onNewTask={() => actions.setShowModal(true)}
          onBulkComplete={actions.handleBulkComplete}
          onBulkDefer={actions.handleBulkDefer}
          onBulkArchive={actions.handleBulkArchive}
          quickAdd={actions.quickAdd}
          setQuickAdd={actions.setQuickAdd}
          quickAddSchedule={actions.quickAddSchedule}
          setQuickAddSchedule={actions.setQuickAddSchedule}
          onQuickAdd={actions.handleQuickAdd}
          loading={data.loading}
        />

        {/* RIGHT: Execution Rail + AI Layer */}
        <div className="hidden xl:flex flex-col" style={{ width: 280, flexShrink: 0 }}>
          <div className="flex-1 min-h-0">
            <TimeExecutionRail
              blocks={data.blocks}
              date={data.today}
              onBlockClick={actions.handleBlockClick}
              onShiftBlock={actions.handleShiftBlock}
              getBusinessColor={getBusinessColor}
              getBusinessShortCode={getBusinessShortCode}
            />
          </div>
          <AIAttentionLayer aiPrompts={data.aiPrompts} summary={data.decisionSummary} />
          <div style={{ borderTop: '1px solid var(--pd-border)' }}>
            <div className="px-4 pt-3 pb-1">
              <div className="text-[10px] uppercase tracking-[0.1em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>Stakeholder Heat</div>
            </div>
            <StakeholderHeat />
          </div>
        </div>
      </div>

      {actions.showModal && (
        <TaskModal isOpen isNew onClose={() => actions.setShowModal(false)} onSave={actions.handleCreateTask} />
      )}
      {actions.editTask && (
        <TaskModal isOpen task={actions.editTask} onClose={() => actions.setEditTask(null)} onSave={actions.handleUpdateTask} />
      )}
      {actions.taskLinkPicker && (
        <TaskLinkPickerModal
          isOpen blockTitle={actions.taskLinkPicker.block?.title}
          candidates={actions.taskLinkPicker.candidates}
          onSelect={(task) => { actions.taskLinkPicker.onSelect(task); actions.setTaskLinkPicker(null); }}
          onClose={() => actions.setTaskLinkPicker(null)}
        />
      )}
      {actions.sweepMode && (
        <BatchSweepMode
          decisions={data.decisions.filter(d => d.category !== 'COMPLETED' && d.category !== 'GRAVEYARD')}
          onAction={actions.handleSweepAction}
          onClose={() => { actions.setSweepMode(false); data.fetchAll(); }}
        />
      )}
      {actions.followUpItem && (
        <FollowUpModal
          completedItem={actions.followUpItem}
          onClose={() => actions.setFollowUpItem(null)}
          onCreateFollowUp={actions.handleCreateFollowUp}
        />
      )}
      {actions.delegateItem && (
        <DelegateModal
          item={actions.delegateItem}
          onClose={() => actions.setDelegateItem(null)}
          onDelegate={actions.handleDelegateTask}
        />
      )}
      <PlanDayPreviewModal
        open={!!actions.planPreview}
        blocks={actions.planPreview?.blocks || []}
        usedFallback={!!actions.planPreview?.usedFallback}
        onAccept={actions.acceptPlanPreview}
        onDiscard={actions.discardPlanPreview}
      />
    </div>
  );
}
